import { FC, useEffect, useMemo, useState } from "react";
import { PaginateModel } from "../../../models/PaginateModel";
import { PaginationLength } from "../../../../_metronic/partials/pagination/PaginationLength";
import { Paginate } from "../../../../_metronic/partials/pagination/Paginate";
import { DEFAULT_PER_PAGE } from "../../../constants/common";
import ConfigModal from "../../../components/Modal/ConfigModal";
import { HeaderModel, ModalModel } from "../../../models/CommonModel";
import TaxAccountTable from "../components/TaxAccountTable";
import { exportTaxBooks, getTaxBooks } from "../AccountBooksCRUD";
import { TaxRowModel, TaxRowSearchModel } from "../../../models/account-books/TaxAccount";
import { download, Toast } from "../../../modules/common/helper";
import { formatPrice } from "../../../selectors";

interface Props {
    opening?: string
    closing?: string
    changeTab: (tab: string) => void
    isFullYear: boolean,
    year: any
}

const TaxAccount: FC<Props> = ({opening, closing, changeTab, isFullYear, year}) => {
    const initSearch = useMemo(() => ({
        page: 1,
        per_page: DEFAULT_PER_PAGE,
        start_date: opening,
        end_date: closing
    }), [opening, closing])
    const [search, setSearch] = useState<TaxRowSearchModel>(initSearch)
    const [data, setData] = useState<PaginateModel<TaxRowModel>>()
    const [subTotal, setSubTotal] = useState<{total_pit_tax: number, total_vat_tax: number, total_paid: number}>();
    const [configModal, setConfigModal] = useState<ModalModel>({
        show: false,
        mode: 'add',
    });
    const [hideCols, setHideCols] = useState<string[]>([]);
    const header: HeaderModel[] = [
        {
            label: 'Số hiệu',
            field: 'voucher_no',
            style: { maxWidth: "150px", minWidth: "150px", },
            sortable: false
        },
        {
            label: 'Ngày tháng',
            field: 'posting_date',
            sortable: false
        },
        {
            label: 'Diễn giải',
            field: 'description',
            style: { maxWidth: "190px", minWidth: "190px", },
            sortable: false
        },
        {
            label: 'Số thuế đã nộp',
            field: 'amount',
            style: { maxWidth: "90px", minWidth: "90px", },
            sortable: false
        },
        {
            label: 'Phương thức',
            field: 'method',
            style: { maxWidth: "90px", minWidth: "90px", },
            sortable: false
        }
    ]

    const handleChangeVisCols = (e: any) => {
        let hiddenCols = [...hideCols];
        if (!e.target?.checked) {
            setHideCols([...hiddenCols, e.target.name])
        } else {
            setHideCols(hiddenCols.filter((item) => item !== e.target.name))
        }
    }

    useEffect(() => {
        if (opening && closing) {
            getData({...initSearch});
        }
    }, [opening, closing, initSearch])

    const getData = async (searchParams: TaxRowSearchModel) => {
        try {
            const { data: resData } = await getTaxBooks(searchParams);
            setData(resData.paginate_data);
            setSubTotal(resData.sub_total);
            setSearch(searchParams)
        } catch (e: any) {
            Toast.fire({
                icon: 'error',
                title: 'Đã có lỗi xảy ra, vui lòng thử lại sau!'
            })
        }
    }
    const handleSearch = () => {

    }

    const handlePageLengthChange = (e: any) => {
        getData({ ...search, page: 1, per_page: +e.target.value })
    }

    const handlePageChange = (data: any) => {
        getData({ ...search, page: data.selected + 1 })
    }

    const handleSort = (sortBy: string, direction: 'asc' | 'desc' | 'none') => {
        const currentSearch = { ...search };
        const sortParams: TaxRowSearchModel = { ...currentSearch, sort_by: sortBy, sort_direction: direction };
        if (direction === 'none') {
            delete sortParams.sort_by;
            delete sortParams.sort_direction;
        }
        getData({ ...sortParams });
    }

    const handleExport = async () => {
        const { data } = await exportTaxBooks({ ...search });
        download(data, 'Sổ thuế');
    }
    return <>
        <div className='card mt-3'>
        <div className="card-header card-header-stretch">
            <h3 className="card-title">
                <button type="button" className="btn btn-primary btn-sm mx-1 mb-1 me-2" onClick={() => changeTab('index')}>
                    <i className="fas fa-angle-left"/> 
                </button>
                Sổ thuế (năm {year})
            </h3>
            <div className="card-toolbar align-items-center">
                <div className="btn-groups">
                    {!isFullYear && (
                        <button className="btn ms-auto btn-sm btn-primary text-hover-white" type="button"
                            onClick={handleExport}>
                            Tải xuống
                        </button>
                    )}
                </div>
            </div>
        </div>
        <div className='card-body'>
            <div className="row justify-content-between align-items-center mb-5">
                <div className='col-12 col-md-6 col-lg-3'>
                    <label className="fw-bolder">Thuế GTGT phải nộp</label><br/>
                    <span className="fs-3">{ formatPrice(subTotal?.total_vat_tax ?? 0) } VNĐ</span>
                </div>
                <div className='col-12 col-md-6 col-lg-3'>
                    <label className="fw-bolder">Thuế TNCN phải nộp</label><br/>
                    <span className="fs-3">{ formatPrice(subTotal?.total_pit_tax ?? 0) } VNĐ</span>
                </div>
                <div className='col-12 col-md-6 col-lg-3'>
                    <label className="fw-bolder">Đã nộp</label><br/>
                    <span className="fs-3">{ formatPrice(subTotal?.total_paid ?? 0) } VNĐ</span>
                </div>
                <div className='col-12 col-md-6 col-lg-3'>
                    <label className="fw-bolder">Còn lại</label><br/>
                    <span className="fs-3">{ formatPrice((subTotal?.total_vat_tax ?? 0) + (subTotal?.total_pit_tax ?? 0) - (subTotal?.total_paid ?? 0) ) } VNĐ</span>
                </div>
            </div>
            <hr/>
            <TaxAccountTable
            data={data?.data || []}
            openColConfig={() => setConfigModal({ ...configModal, show: true })}
            header={header}
            hideCols={hideCols}
            handleSort={handleSort}
            />
        </div>
        <div className='card-footer d-flex justify-content-between paginate-wrapper'>
            <PaginationLength selected={search.per_page || DEFAULT_PER_PAGE} onChange={handlePageLengthChange} />
            <Paginate
                pageCount={data?.meta?.last_page || 0}
                onPageChange={handlePageChange}
                forcePage={(search.page || 1) - 1}
            />
        </div>
        <ConfigModal
            {...configModal}
            header={header}
            hideCols={hideCols}
            handleChangeVisCols={handleChangeVisCols}
            onClose={() => { setConfigModal({ ...configModal, show: false }) }}
        />
        </div>
    </>
}

export default TaxAccount;